import React, { useEffect } from "react";
import { Link, useParams, Navigate } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowLeft, MapPin, Plane, Clock, Shield, ArrowRight, Check } from "lucide-react";
import { findCity, CITIES } from "../data/cities";
import { useLang } from "../i18n/LanguageContext";
import Header from "../components/Header";
import Footer from "../components/Footer";
import HowItWorks from "../components/HowItWorks";
import CostCalculator from "../components/CostCalculator";
import Contact from "../components/Contact";

const PLANE_IMG = "https://customer-assets.emergentagent.com/job_fly-ship-cr/artifacts/3xo1ic5d_image.png";

const CityPage = () => {
  const { slug } = useParams();
  const { t, lang } = useLang();
  const city = findCity(slug);

  // Per-page SEO meta — title, description, canonical, JSON-LD
  useEffect(() => {
    if (!city) return;

    const title =
      lang === "es"
        ? `Envíos a ${city.name}, Costa Rica | $15/kg desde Miami · Ship Costa Rica`
        : `Shipping to ${city.name}, Costa Rica | $15/kg from Miami · Ship Costa Rica`;
    const desc =
      lang === "es"
        ? `Servicio confiable de envío aéreo de Miami a ${city.name}, ${city.region}. Compra en Amazon, eBay, SHEIN y entregamos en tu puerta en 5–7 días por $15 por kilo.`
        : `Trusted air shipping from Miami to ${city.name}, ${city.region}. Shop Amazon, eBay, SHEIN and we deliver to your door in 5–7 days for $15 per kilo.`;

    document.title = title;

    const setMeta = (selector, attr, value) => {
      let el = document.querySelector(selector);
      if (!el) {
        el = document.createElement("meta");
        const [, , key] = selector.match(/\[(name|property)="([^"]+)"\]/) || [];
        if (key) el.setAttribute(selector.includes("property") ? "property" : "name", key);
        document.head.appendChild(el);
      }
      el.setAttribute(attr, value);
    };
    setMeta('meta[name="description"]', "content", desc);
    setMeta('meta[property="og:title"]', "content", title);
    setMeta('meta[property="og:description"]', "content", desc);
    setMeta('meta[name="twitter:title"]', "content", title);
    setMeta('meta[name="twitter:description"]', "content", desc);

    let canonical = document.querySelector('link[rel="canonical"]');
    if (canonical) canonical.setAttribute("href", `https://shipcr.com/shipping-to/${city.slug}`);

    // City-specific JSON-LD
    const jsonLd = document.getElementById("city-jsonld");
    const data = {
      "@context": "https://schema.org",
      "@type": "Service",
      name: `Air Shipping to ${city.name}, Costa Rica`,
      description: desc,
      provider: {
        "@type": "Organization",
        name: "Ship Costa Rica",
        url: "https://shipcr.com/",
      },
      areaServed: {
        "@type": "City",
        name: `${city.name}, ${city.region}`,
        geo: { "@type": "GeoCoordinates", latitude: city.lat, longitude: city.lng },
      },
      offers: {
        "@type": "Offer",
        price: "15",
        priceCurrency: "USD",
        priceSpecification: {
          "@type": "UnitPriceSpecification",
          price: "15",
          priceCurrency: "USD",
          unitCode: "KGM",
        },
      },
    };
    if (jsonLd) {
      jsonLd.textContent = JSON.stringify(data);
    } else {
      const s = document.createElement("script");
      s.type = "application/ld+json";
      s.id = "city-jsonld";
      s.textContent = JSON.stringify(data);
      document.head.appendChild(s);
    }

    return () => {
      const s = document.getElementById("city-jsonld");
      if (s) s.remove();
    };
  }, [city, lang]);

  if (!city) return <Navigate to="/" replace />;

  const intro = city.intro[lang];
  const bullet = city.bullet[lang];
  const otherCities = CITIES.filter((c) => c.slug !== city.slug).slice(0, 6);

  return (
    <div className="min-h-screen bg-brand-cream text-brand-navy">
      <Header />
      <main>
        {/* City hero */}
        <section
          data-testid={`city-hero-${city.slug}`}
          className="relative pt-32 pb-20 sm:pt-40 sm:pb-28 overflow-hidden"
        >
          <div className="absolute inset-0 sky-gradient" aria-hidden="true" />
          <div className="absolute inset-x-0 top-0 h-32 pointer-events-none" style={{ background: "linear-gradient(180deg, rgba(245,239,232,0.7) 0%, rgba(245,239,232,0) 100%)" }} aria-hidden="true" />
          <div className="absolute inset-x-0 bottom-0 h-40 pointer-events-none" style={{ background: "linear-gradient(180deg, rgba(245,239,232,0) 0%, rgba(245,239,232,0.85) 100%)" }} aria-hidden="true" />

          <motion.img
            src={PLANE_IMG}
            alt=""
            initial={{ opacity: 0, x: 100, y: -30 }}
            animate={{ opacity: 1, x: 0, y: 0 }}
            transition={{ duration: 1.2, ease: [0.16, 1, 0.3, 1] }}
            className="hidden sm:block absolute top-24 -right-8 w-[260px] lg:w-[360px] scale-x-[-1] drop-shadow-[0_22px_28px_rgba(14,42,86,0.3)]"
            aria-hidden="true"
          />

          <div className="relative max-w-5xl mx-auto px-5 sm:px-8 lg:px-12">
            <Link
              to="/"
              data-testid="city-back-home"
              className="inline-flex items-center gap-2 rounded-full bg-white/80 backdrop-blur-md border border-brand-navy/15 px-4 py-2 text-xs font-bold uppercase tracking-[0.18em] text-brand-navy hover:bg-brand-navy hover:text-brand-cream transition"
            >
              <ArrowLeft size={13} /> {lang === "es" ? "Inicio" : "Home"}
            </Link>

            <motion.div
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.7, delay: 0.1 }}
              className="mt-8 inline-flex items-center gap-2 text-xs font-bold uppercase tracking-[0.2em] text-brand-red"
            >
              <MapPin size={14} /> {city.region}
            </motion.div>

            <motion.h1
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.2, ease: [0.16, 1, 0.3, 1] }}
              data-testid="city-headline"
              className="mt-3 font-heading font-black text-4xl sm:text-6xl lg:text-7xl tracking-[-0.03em] text-brand-navy leading-[1.02]"
            >
              {lang === "es" ? "Envíos a" : "Shipping to"}{" "}
              <span className="text-brand-red">{city.name}</span>
            </motion.h1>

            <motion.p
              initial={{ opacity: 0, y: 24 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.7, delay: 0.35 }}
              className="mt-6 max-w-2xl text-base sm:text-lg text-brand-navy/75 leading-relaxed"
            >
              {intro}
            </motion.p>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.5 }}
              className="mt-9 grid grid-cols-3 gap-3 sm:gap-4 max-w-2xl"
            >
              {[
                { v: "$15/kg", l: lang === "es" ? "Aéreo" : "Air rate", i: Plane },
                { v: "5–7", l: lang === "es" ? "días entrega" : "day delivery", i: Clock },
                { v: "24/7", l: lang === "es" ? "Soporte" : "Support", i: Shield },
              ].map((s, i) => (
                <div key={i} className="rounded-2xl bg-white/85 backdrop-blur-md border border-brand-navy/10 p-4 sm:p-5">
                  <s.i size={16} className="text-brand-red mb-2" />
                  <div className="font-heading font-black text-lg sm:text-2xl text-brand-navy">{s.v}</div>
                  <div className="text-[10px] sm:text-xs uppercase tracking-wider text-brand-navy/60 font-semibold mt-0.5">
                    {s.l}
                  </div>
                </div>
              ))}
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.65 }}
              className="mt-10 flex flex-col sm:flex-row gap-4"
            >
              <a
                href="https://trackmypackage.app/join/shipcostarica" target="_blank" rel="noopener noreferrer"
                data-testid="city-cta-primary"
                className="group inline-flex items-center justify-center gap-2 rounded-full bg-brand-red text-white font-heading font-bold text-base px-7 py-4 shadow-[0_12px_30px_-10px_rgba(216,35,42,0.55)] hover:bg-brand-navy transition-all"
              >
                {lang === "es" ? `Empezar a enviar a ${city.name}` : `Start shipping to ${city.name}`}
                <ArrowRight size={18} className="group-hover:translate-x-1 transition-transform" />
              </a>
              <a
                href="#calculator"
                className="inline-flex items-center justify-center rounded-full bg-white/80 backdrop-blur-md border border-brand-navy/20 text-brand-navy font-heading font-bold text-base px-7 py-4 hover:bg-white"
              >
                {lang === "es" ? "Calcular precio" : "Calculate price"}
              </a>
            </motion.div>
          </div>
        </section>

        {/* Why ship here */}
        <section className="relative py-16 sm:py-20 bg-white">
          <div className="max-w-5xl mx-auto px-5 sm:px-8 lg:px-12">
            <h2 className="font-heading font-black text-3xl sm:text-4xl tracking-tight text-brand-navy leading-tight max-w-2xl">
              {lang === "es"
                ? `¿Por qué enviar a ${city.name} con Ship Costa Rica?`
                : `Why ship to ${city.name} with Ship Costa Rica?`}
            </h2>
            <ul className="mt-8 grid grid-cols-1 md:grid-cols-2 gap-4">
              {[
                bullet,
                lang === "es"
                  ? `Tarifa única de $15 por kilo · sin sorpresas`
                  : `Flat $15 per kilo rate · no surprise fees`,
                lang === "es"
                  ? `Compra en Amazon, eBay, SHEIN, Walmart y más`
                  : `Shop Amazon, eBay, SHEIN, Walmart and more`,
                lang === "es"
                  ? `Seguimiento en tiempo real desde Miami a ${city.name}`
                  : `Real-time tracking from Miami to ${city.name}`,
                lang === "es"
                  ? `Trámites aduanales incluidos · última milla pagada al courier`
                  : `Customs handling included · last-mile paid to courier`,
                lang === "es"
                  ? `Atención al cliente 24/7 en español e inglés`
                  : `24/7 customer service in Spanish and English`,
              ].map((b, i) => (
                <li
                  key={i}
                  data-testid={`city-bullet-${i}`}
                  className="flex items-start gap-3 rounded-2xl bg-brand-cream border border-brand-navy/10 p-5"
                >
                  <span className="flex-shrink-0 w-7 h-7 rounded-full bg-brand-red text-white flex items-center justify-center mt-0.5">
                    <Check size={15} strokeWidth={3} />
                  </span>
                  <span className="text-base text-brand-navy/85 font-medium leading-relaxed">{b}</span>
                </li>
              ))}
            </ul>
          </div>
        </section>

        <CostCalculator />
        <HowItWorks />
        <Contact />

        {/* Other cities */}
        <section className="bg-white py-16">
          <div className="max-w-6xl mx-auto px-5 sm:px-8 lg:px-12">
            <h2 className="font-heading font-black text-2xl sm:text-3xl tracking-tight text-brand-navy mb-6">
              {lang === "es" ? "Otras ciudades que servimos" : "Other cities we ship to"}
            </h2>
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3">
              {otherCities.map((c) => (
                <Link
                  key={c.slug}
                  to={`/shipping-to/${c.slug}`}
                  data-testid={`city-related-${c.slug}`}
                  className="rounded-2xl bg-brand-cream border border-brand-navy/10 p-4 hover:bg-brand-navy hover:text-brand-cream transition-all"
                >
                  <MapPin size={14} className="text-brand-red mb-2" />
                  <div className="font-heading font-bold text-sm leading-tight">{c.name}</div>
                </Link>
              ))}
            </div>
          </div>
        </section>
      </main>
      <Footer />
    </div>
  );
};

export default CityPage;
